<!--
  File name: ViewFilesModal.vue
  Program Description:
    - This component allows the user to view the uploaded files during document sharing.
    - It uses iframe tag as the PDF viewer
-->
<template>
  <v-dialog
    v-model="viewFile"
    max-width="1000px"
    persistent
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        color="primary"
        dark
        text
        v-bind="attrs"
        :loading="loading"
        v-on="on"
      >
        <v-icon class="mr-1">
          mdi-eye
        </v-icon>
        View Documents
      </v-btn>
    </template>
    <v-card
      class="pa-12"
      style="position: relative"
    >
      <div
        style="position: absolute; top: 0; left: 0; width: 100%;"
        class="pa-3 d-flex justify-end"
      >
        <v-btn
          icon
          @click="viewFile = false"
        >
          <v-icon>
            mdi-close
          </v-icon>
        </v-btn>
      </div>
      <v-row>
        <v-col class="pb-0 pl-8">
          <h2 style="color: #162b54">
            Implementation of Security Measures / Controls
          </h2>
        </v-col>
      </v-row>

      <v-row
        v-if="noData"
        class="pa-12"
      >
        <NoDataYet
          :image="implem"
          message="No data yet"
        />
      </v-row>

      <v-row
        v-if="formItem.fullReport && formItem.fullReport.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-8"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.fullReport"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Full Report"
            file-id="fullReport"
            field-name="fullReport"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.privacyImpactAssessment && formItem.privacyImpactAssessment.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-8"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.privacyImpactAssessment"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Privacy Impact Assessment"
            file-id="privacyImpactAssessment"
            field-name="privacyImpactAssessment"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.securityIncidentManagementPolicy && formItem.securityIncidentManagementPolicy.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-8"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.securityIncidentManagementPolicy"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Security Incident Management Policy"
            file-id="securityIncidentManagementPolicy"
            field-name="securityIncidentManagementPolicy"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row v-if="showTitle">
        <v-col class="pb-0 pl-8">
          <h3>
            Sections of the Privacy Manual containing controls/policies/measures on, if any:
          </h3>
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.operationsSecurity && formItem.operationsSecurity.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-16"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.operationsSecurity"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Operations Security (Protection from malware; backup; logging and monitoring)"
            file-id="operationsSecurity"
            field-name="operationsSecurity"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.systemAcquisition && formItem.systemAcquisition.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-16"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.systemAcquisition"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="System Acquisition, Development & Maintenance (Security in Development and Support Processes)"
            file-id="systemAcquisition"
            field-name="systemAcquisition"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.accessControl && formItem.accessControl.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-16"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.accessControl"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Access Control (User Responsibilities; System & Application Access Control)"
            file-id="accessControl"
            field-name="accessControl"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.communicationsSecurity && formItem.communicationsSecurity.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-16"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.communicationsSecurity"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Communications Security (Network Security Management, Information Transfer)"
            file-id="communicationsSecurity"
            field-name="communicationsSecurity"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.physicalAndEnvironmentalSecurity && formItem.physicalAndEnvironmentalSecurity.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-16"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.physicalAndEnvironmentalSecurity"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Physical and Environmental Security (e.g. Means of Storage, Security Measures, Form of Information Stored, Storage Type and Location)"
            file-id="physicalAndEnvironmentalSecurity"
            field-name="physicalAndEnvironmentalSecurity"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.assetManagement && formItem.assetManagement.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-16"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.assetManagement"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Asset Management (Media Handling, Acceptable Use of Assets)"
            file-id="assetManagement"
            field-name="assetManagement"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.cryptography && formItem.cryptography.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-16"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.cryptography"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Cryptography"
            file-id="cryptography"
            field-name="cryptography"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.humanResourceSecurity && formItem.humanResourceSecurity.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-16"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.humanResourceSecurity"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Human Resource Security (Information Awareness, Education & Training, Disciplinary Process)"
            file-id="humanResourceSecurity"
            field-name="humanResourceSecurity"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.compliance && formItem.compliance.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-16"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.compliance"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Compliance (Legal & Contractual Requirements – Cybercrime, Procedure for Reporting Malicious Attacks)"
            file-id="compliance"
            field-name="compliance"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.informationSecurityPolicy && formItem.informationSecurityPolicy.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-8"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.informationSecurityPolicy"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Information Security Policy for the Third-Party Service Provider;"
            file-id="informationSecurityPolicy"
            field-name="informationSecurityPolicy"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.dataSubjectNotification && formItem.dataSubjectNotification.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-8"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.dataSubjectNotification"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Copy of Data Subject Notification and its Proof of Sending"
            file-id="dataSubjectNotification"
            field-name="dataSubjectNotification"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.delayInNotificationExplanation && formItem.delayInNotificationExplanation.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-8"
        >
          <FileInputUpload
            input-class="required-field"
            :value="formItem.delayInNotificationExplanation"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            accept=".pdf"
            label="Explanation as to why there was delay in notification."
            file-id="delayInNotificationExplanation"
            field-name="delayInNotificationExplanation"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.othersAttachment && formItem.othersAttachment.length"
        class="pt-0"
      >
        <v-col class="pb-0 pl-9">
          <h3>
            Others:
          </h3>
        </v-col>
      </v-row>

      <v-row
        v-if="formItem.othersAttachment && formItem.othersAttachment.length"
        class="pt-0"
      >
        <v-col
          class="pb-0 pl-8"
        >
          <DynamicFileInput
            input-class="required-field"
            :value="formItem.othersAttachment"
            :dense="true"
            :clearable="true"
            :can-edit="false"
            :show-download="true"
            :entry-id="formItem._id"
            can-upload
            is-document-submitted
            accept=".doc, .docx, .ppt, .pptx, .pdf, .xls, .xlsx, .png, .jpg, .jpeg"
            label="Explanation as to why there was delay in notification."
            file-id="othersAttachment"
            field-name="othersAttachment"
            collection="pdbnf"
            can-view-file
          />
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import implem from '@/assets/implem.png'
export default {
  props: {
    formItem: {
      type: Object,
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    viewFile: false
  }),
  computed: {
    showTitle () {
      const f = this.formItem
      const show = (f.operationsSecurity && f.operationsSecurity.length) ||
      (f.systemAcquisition && f.systemAcquisition.length) ||
      (f.accessControl && f.accessControl.length) ||
      (f.communicationsSecurity && f.communicationsSecurity.length) ||
      (f.physicalAndEnvironmentalSecurity && f.physicalAndEnvironmentalSecurity.length) ||
      (f.assetManagement && f.assetManagement.length) ||
      (f.cryptography && f.cryptography.length) ||
      (f.humanResourceSecurity && f.humanResourceSecurity.length) ||
      (f.compliance && f.compliance.length)
      return show
    },
    noData () {
      const f = this.formItem
      const show = (f.fullReport && f.fullReport.length) ||
      (f.privacyImpactAssessment && f.privacyImpactAssessment.length) ||
      (f.securityIncidentManagementPolicy && f.securityIncidentManagementPolicy.length) ||
      (f.informationSecurityPolicy && f.informationSecurityPolicy.length) ||
      (f.dataSubjectNotification && f.dataSubjectNotification.length) ||
      (f.delayInNotificationExplanation && f.delayInNotificationExplanation.length) ||
      (f.othersAttachment && f.othersAttachment.length)
      return !(show || this.showTitle)
    },
    implem () {
      return implem
    }
  }
}
</script>
